/**
 * The three states between opening this page and seeing the fleet.
 *
 * 1. **Not signed in.** A login button and nothing else.
 * 2. **Signed in, but not a platform operator.** The controller answers 403
 *    from `/api/me`, and this is the state that must never degrade into an
 *    empty dashboard: zero centers, zero findings and a green header reads as
 *    "the fleet is healthy" when it actually means "you cannot see it".
 * 3. **Authorised.** The app renders.
 *
 * A 401 is kept distinct from a 403 all the way to the screen, because the
 * controller distinguishes them deliberately: 401 is "we do not know who you
 * are", 403 is "we do, and you are not an operator". Collapsing them makes a
 * misconfigured admin list look like a broken login.
 */

import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { Button } from './ui/Button';
import { Callout } from './Callout';
import { TextInput } from './ui/Form';
import { Card } from './ui/Card';
import { LoadingBlock } from './States';

import { ApiError, describeError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useResource } from '../lib/useResource';
import type { MeResponse } from '../types/api';

export function Gate({
  title,
  children,
  actions,
  footnote,
  live = false,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  footnote?: ReactNode;
  live?: boolean;
}) {
  return (
    <div className="gate">
      <Card className="gate__card" role={live ? 'alert' : undefined}>
        <div className="gate__brand">
          <span className="gate__brand-mark" aria-hidden="true">
            H
          </span>
          <span>Health-360 Fleet Controller</span>
        </div>
        <h1 className="gate__title">{title}</h1>
        <div className="gate__body">{children}</div>
        {actions ? <div className="gate__actions">{actions}</div> : null}
        {footnote ? <div className="gate__footnote">{footnote}</div> : null}
      </Card>
    </div>
  );
}

/**
 * The sign-in form.
 *
 * Deliberately says as little as the server does. Every rejection comes back
 * with one string — unknown address, invited-but-never-enrolled, and wrong
 * password are indistinguishable — because anything more helpful here is a way
 * to enumerate who the operators are.
 */
function LoginGate() {
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await auth.login(email.trim(), password);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Gate
      title="Sign in to continue"
      footnote={
        <>
          This control plane can read every clinic&rsquo;s configuration and can
          restart their containers. It is reachable only from the private
          network, and signing in is not by itself enough — you also need a{' '}
          <code>platform_admins</code> record.
        </>
      }
    >
      <form className="stack" onSubmit={submit}>
        {error ? (
          <Callout tone="critical" live title="Could not sign you in">
            {error}
          </Callout>
        ) : null}
        <TextInput
          id="login-email"
          labelText="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={busy}
        />
        <TextInput
          id="login-password"
          labelText="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={busy}
        />
        <div className="toolbar">
          <Button type="submit" size="lg" disabled={busy || !email || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
          <a className="field__help" href="/set-password">
            Set or reset your password
          </a>
        </div>
      </form>
    </Gate>
  );
}


export function AuthGate({
  children,
}: {
  children: (me: MeResponse) => ReactNode;
}) {
  const auth = useAuth();

  // Always asked, with no client-side precondition. The session is an HttpOnly
  // cookie this code cannot read, so the only way to know whether one is
  // present and valid is to ask the server that issued it — and a controller
  // running `dev_no_auth` answers here without a cookie, which is exactly how
  // dev mode is discovered rather than configured.
  const me = useResource<MeResponse>('/api/me');

  // ── 1. not signed in ──────────────────────────────────────────────────────
  if (me.error instanceof ApiError && me.error.isUnauthenticated) {
    return <LoginGate />;
  }

  if (me.loading) {
    return (
      <Gate title="Checking your access">
        <LoadingBlock label="Asking the controller whether this account is a platform operator…" />
      </Gate>
    );
  }

  // ── 2. signed in, not authorised ──────────────────────────────────────────
  if (me.error instanceof ApiError && me.error.isForbidden) {
    return (
      <Gate
        title="You are not a platform operator"
        live
        actions={
          <>
            <Button kind="tertiary" onClick={me.reload}>
              Check again
            </Button>
            <Button kind="ghost" onClick={auth.logout}>
              Sign out
            </Button>
          </>
        }
        footnote={
          <>
            Access comes from a <code>platform_admins</code> row for your email.
            Ask an owner to add you.
          </>
        }
      >
        <p>
          Your sign-in worked
          {auth.accountEmail ? (
            <>
              {' '}
              — you are authenticated as <strong>{auth.accountEmail}</strong>
            </>
          ) : null}
          , but this account is not authorised to read the fleet.
        </p>
        <p>
          Nothing is shown rather than an empty dashboard on purpose: no centers,
          no findings and no alerts would look exactly like a healthy fleet.
        </p>
        <p className="muted">
          <span className="mono">{me.error.detail}</span>
        </p>
      </Gate>
    );
  }

  if (me.error) {
    const network = me.error instanceof ApiError && me.error.isNetwork;
    return (
      <Gate
        title={
          network
            ? 'Cannot reach the fleet controller'
            : 'Could not confirm your access'
        }
        live
        actions={<Button onClick={me.reload}>Try again</Button>}
        footnote={
          network ? (
            <>
              The controller binds to a private address and is deliberately not
              served through the fleet&rsquo;s Caddy, so it is unreachable from
              the public internet even when everything is running.
            </>
          ) : null
        }
      >
        <p>{describeError(me.error)}</p>
      </Gate>
    );
  }

  if (!me.data) {
    return (
      <Gate title="Checking your access">
        <LoadingBlock label="Loading…" />
      </Gate>
    );
  }

  // ── 3. authorised ─────────────────────────────────────────────────────────
  return <>{children(me.data)}</>;
}
